import { ChangeDetectionStrategy, Component, computed, inject, LOCALE_ID, PLATFORM_ID, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DecimalPipe, isPlatformBrowser } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import {
  AlertService,
  ButtonLoadingDirective,
  CardActionsComponent,
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { VipLevelConfig, VipLevelUpClaim } from '@gains-com/shared';
import { GoldCoinsIconComponent } from '../../../../components/coins/gold-coins-icon.component.js';
import { SweepsCoinsIconComponent } from '../../../../components/coins/sweeps-coins-icon.component.js';
import { VipViewService } from '../../../../core/view-states/vip-view.service.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';
import { vipBadgeUrl } from '../../vip-assets.js';
import { accountTimeoutMessage, accountTimeoutRejectionMessage } from '../../../../utils/timeout.js';

export interface LevelUpRewardDialogData {
  /** The unclaimed level-up reward to surface (SC/GC amounts, tier identity). */
  claim: VipLevelUpClaim;
  /** Resolved tier config for the claim — drives the title, badge and color. */
  config: VipLevelConfig | null;
}

/**
 * Level-up reward claim dialog. Web mirror of the Figma "Level Up Reward
 * [Bronze]" sheet: a tier-colored badge, the SC + GC the user earned for
 * reaching the tier, and a Claim Reward / Share pair of CTAs. On a successful
 * claim it closes with `true` so the parent can refresh its reward state.
 *
 * Opened via `DialogService` (never raw CDK `Dialog`) — see the
 * `web-dialog-pattern` skill.
 */
@Component({
  selector: 'app-level-up-reward-dialog',
  templateUrl: './level-up-reward-dialog.component.html',
  styleUrl: './level-up-reward-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardComponent,
    CardContentComponent,
    CardActionsComponent,
    MatButton,
    DecimalPipe,
    ButtonLoadingDirective,
    IconComponent,
    GoldCoinsIconComponent,
    SweepsCoinsIconComponent,
    CardHeaderComponent,
    MatTooltip,
  ],
})
export class LevelUpRewardDialogComponent {
  private readonly _dialogRef = inject<DialogRef<boolean>>(DialogRef);
  private readonly _data = inject<LevelUpRewardDialogData>(DIALOG_DATA);
  private readonly _vipView = inject(VipViewService);
  private readonly _userView = inject(UserViewService);
  private readonly _alert = inject(AlertService);
  private readonly _logger = inject(LOGGER_FACTORY).create('LevelUpRewardDialogComponent');
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _locale = inject(LOCALE_ID);

  readonly claim = this._data.claim;
  readonly config = this._data.config;
  /** Tier display name; falls back to the claim's raw level identity. */
  readonly title = this.config?.title ?? this.claim.level_identity;
  /** Tier accent color; falls back to the theme primary when unconfigured. */
  readonly color = this.config?.color || 'var(--mat-sys-primary)';
  /** Tier badge — per-tier `logo.webp` when keyed, else the stored `logo_url`. */
  readonly badgeUrl = this.config ? vipBadgeUrl(this.config) : null;

  readonly claiming = signal(false);

  private readonly _user = toSignal(this._userView.state$);
  /** Active responsible-gaming Timeout message — disables the Claim CTA. */
  readonly timeout = computed(() => accountTimeoutMessage(this._user()?.timeout_until, this._locale));

  async claimReward(): Promise<void> {
    if (this.claiming() || this.timeout()) {
      return;
    }

    this.claiming.set(true);

    try {
      await this._vipView.claimLevelReward(this.claim.vip_level_up_claim_id);
      this._alert.notifySuccess('Reward Claimed', 'Successfully claimed your VIP reward.');
      this._dialogRef.close(true);
    } catch (err: unknown) {
      const code = (err as { error?: { code?: string } })?.error?.code;
      if (code === 'ACCOUNT_TIMEOUT') {
        this._alert.notifyDanger(
          'Account Timeout',
          accountTimeoutRejectionMessage(this._user()?.timeout_until, this._locale),
        );
        await this._userView.syncTimeout();
      } else {
        const message =
          code === 'VIP_LEVEL_UP_CLAIM_ALREADY_CLAIMED'
            ? 'This reward has already been claimed.'
            : 'An error has occurred. Please try again later.';
        this._alert.notifyDanger('Error', message);
      }
      this._logger.error(`Level reward claim failed: ${err}`);
    } finally {
      this.claiming.set(false);
    }
  }

  share(): void {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    const text = `I just reached ${this.title} VIP on Gains.com!`;
    const url = `${window.location.origin}/vip`;
    const nav = navigator as Navigator & {
      share?: (data: { title: string; text: string; url: string }) => Promise<void>;
    };

    if (nav.share) {
      nav.share({ title: 'Gains.com VIP', text, url }).catch(() => {
        /* user dismissed the native share sheet */
      });
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(`${text} ${url}`).then(
        () => this._alert.notifySuccess('Copied', 'Share text copied to your clipboard.'),
        () => {
          /* clipboard unavailable */
        },
      );
    }
  }

  close(): void {
    this._dialogRef.close(false);
  }
}
